<template>
  <v-main class="vs-login-wrapper">
    <!-- Ambient Lighting Elements -->
    <div class="vs-login-glow-1" aria-hidden="true"></div>
    <div class="vs-login-glow-2" aria-hidden="true"></div>
    <div class="vs-login-grid-bg" aria-hidden="true"></div>

    <v-container class="fill-height position-relative" fluid>
      <v-row no-gutters align="center" justify="center">
        <v-col cols="12" sm="10" md="7" lg="5" xl="4">
          
          <div v-if="error" class="mb-4">
            <app-alert @dismissed="onDismissed" :error="error.message"></app-alert>
            <v-alert v-if="error" :value="true" type="error" closable @click:close="onDismissed" class="rounded-xl vs-alert-custom">
              {{ error.message }}
              <div v-if="error.code === 'auth/email-not-verified'" class="mt-3 text-center">
                <v-btn
                  color="white"
                  variant="outlined"
                  size="small"
                  :loading="resendingEmail"
                  @click.stop="resendEmail"
                  style="text-transform: none; font-family: 'Inter', sans-serif; border-radius: 20px;"
                >
                  <v-icon start size="18">mdi-email-sync-outline</v-icon>
                  Reenviar e-mail de confirmação
                </v-btn>
              </div>
            </v-alert>
          </div>

          <!-- Glassmorphic Luxury Card -->
          <v-card class="vs-login-card px-6 px-sm-10 py-10" elevation="24">
            
            <!-- Brand Lockup -->
            <div class="text-center mb-8">
              <div class="vs-logo-container d-flex flex-column align-center justify-center">
                <img
                  src="/veriSalvatori/logo_site.svg"
                  alt="Veri Salvatori"
                  class="vs-login-logo"
                  style="max-width: 260px; height: 38px; width: auto; object-fit: contain;"
                />
              </div>
              <div class="vs-login-subtitle">INSTITUTO MÉDICO</div>
              <div class="vs-login-tagline">Plataforma de Questões e Simulados</div>
            </div>

            <v-form
              v-model="form"
              @submit.prevent="onSubmit"
            >
              <div class="vs-input-wrapper mb-3">
                <label class="vs-input-label">E-mail de acesso</label>
                <v-text-field
                  v-model="email"
                  :readonly="loading"
                  :rules="[rules.required, rules.email]"
                  clearable
                  placeholder="seu.email@exemplo.com"
                  prepend-inner-icon="mdi-email-outline"
                  variant="outlined"
                  density="comfortable"
                  class="vs-custom-field"
                  hide-details="auto"
                ></v-text-field>
              </div>

              <div v-if="!forgotPassword" class="vs-input-wrapper mb-2">
                <label class="vs-input-label">Sua senha</label>
                <v-text-field
                  v-model="password"
                  :append-inner-icon="show ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                  :readonly="loading"
                  :rules="[rules.required, rules.min]"
                  :type="show ? 'text' : 'password'"
                  clearable
                  placeholder="Digite sua senha"
                  @click:append-inner="show = !show"
                  prepend-inner-icon="mdi-lock-outline"
                  variant="outlined"
                  density="comfortable"
                  class="vs-custom-field"
                  hide-details="auto"
                ></v-text-field>
              </div>

              <div v-if="!forgotPassword" class="d-flex justify-end mb-5">
                <button
                  type="button"
                  @click="showForgotPassword"
                  class="vs-forgot-link"
                >
                  Alterar ou redefinir sua senha
                </button>
              </div>
              
              <v-btn
                v-if="!forgotPassword"
                :disabled="!form"
                :loading="loading"
                block
                class="vs-btn-submit"
                type="submit"
                elevation="4"
              >
                <span>Acessar Plataforma</span>
                <v-icon end size="18" class="ml-2">mdi-arrow-right</v-icon>
              </v-btn>
            </v-form>

            <v-btn
              v-if="showForgot"
              block
              :disabled="!form"
              class="vs-btn-submit mt-4"
              @click="confirmForgotPassword"
            >
              <span>Confirmar Redefinição</span>
            </v-btn>

            <div v-if="!forgotPassword">
              <div class="vs-divider-box my-7">
                <div class="vs-divider-line"></div>
                <span class="vs-divider-text">NOVO POR AQUI?</span>
                <div class="vs-divider-line"></div>
              </div>

              <div class="text-center">
                <p class="vs-trial-caption">
                  Conheça a metodologia e o banco de questões Veri Salvatori
                </p>
                <v-btn
                  block
                  class="vs-btn-trial"
                  @click="showTrialDialog = true"
                  elevation="0"
                >
                  <v-icon start size="18" class="mr-2">mdi-sparkles</v-icon>
                  Experimente 7 dias grátis
                </v-btn>
              </div>
            </div>

            <!-- Footer Badge -->
            <div class="text-center mt-8">
              <span class="vs-card-footer-note">
                <v-icon size="14" class="mr-1" color="#8296b9">mdi-shield-check-outline</v-icon>
                Ambiente seguro &bull; Veri Salvatori
              </span>
            </div>

          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <ConfirmationDialog :dialogVisible="showConfirmationDialog" @close="closeConfirmationDialog" :email="email" />
    <FreeTrialDialog :dialogVisible="showTrialDialog" @close="showTrialDialog = false" @success="onTrialSuccess" />

    <v-snackbar v-model="trialSuccess" color="success" top :timeout="4000">
      <span style="font-family: 'Inter', sans-serif;">Dados enviados com sucesso! Nossa equipe entrará em contato.</span>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="trialSuccess = false">
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </v-main>
</template>

<script>
import ConfirmationDialog from '../components/Shared/PasswordReset.vue';
import FreeTrialDialog from '../components/Shared/FreeTrialDialog.vue';

export default {
  components: {
    ConfirmationDialog,
    FreeTrialDialog,
  },
  data: () => ({
    email: "",
    password: "",
    form: false,
    show: false,
    showConfirmationDialog: false,

    rules: {
      required: value => !!value || 'Campo Obrigátorio.',
      min: v => v.length >= 6 || 'Sua senha deve ter no mínimo 6 caracteres.',
      email: value => {
        const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        return pattern.test(value) || 'E-mail Inválido.'
      },
    },
    forgotPassword: false,
    showForgot: false,
    showTrialDialog: false,
    trialSuccess: false,
    resendingEmail: false,
  }),

  methods: {
    onTrialSuccess() {
      this.trialSuccess = true;
    },
    async resendEmail() {
      if (!this.email || !this.password) return;
      this.resendingEmail = true;
      try {
        await this.$store.dispatch('resendVerificationEmail', {
          email: this.email,
          password: this.password
        });
        alert('E-mail de confirmação reenviado com sucesso! Verifique sua caixa de entrada e lixo eletrônico (spam).');
      } catch (err) {
        alert('Erro ao reenviar o e-mail. Verifique se a senha está correta e tente novamente.');
      } finally {
        this.resendingEmail = false;
      }
    },
    onSubmit() {
      if (!this.form) return;

      let payload = {
        email: this.email,
        password: this.password,
      };
      this.$store.dispatch('signUserIn', payload);
    },
    onDismissed() {
      this.$store.dispatch('clearError');
    },
    showForgotPassword() {
      this.forgotPassword = true;
      this.showForgot = true;
    },
    confirmForgotPassword() {
      this.$store.dispatch('forgotPassword', {email: this.email})
          
      // For example, sending an email or redirecting to a new page
      this.showConfirmationDialog = true;

      // Then reset the fields and show the original form
     // this.email = '';
      this.password = '';
      this.forgotPassword = false;
      this.showForgot = false;
    },
    closeConfirmationDialog() {
      // Close the confirmation dialog
      this.showConfirmationDialog = false;
    },
  },
  computed: {
    user() {
      return this.$store.getters.user;
    },
    error() {
      return this.$store.getters.error;
    },
    loading() {
      return this.$store.getters.loading;
    },
  },
  watch: {
    async user(value) {
      if (this.$store.state.isRegisteringTrial) return;
      if (value !== null && value !== undefined) {
        if (value.email) {
          let userName = value.email.split('@')[0];
          if (userName == 'eventcenter') {
            this.$router.push('/webapp/event');
            return;
          }
        }
        try {
          await this.$store.dispatch('checkAdmin');
          const admin = (this.$store.getters.admin || '').toLowerCase().trim();
          const kitList = ['kit_temfc', 'kit_temfcadmin', 'kit_temfc_admin', 'kit-temfc', 'kit-temfcadmin', 'kit-temfc-admin', 'kittemfc', 'kittemfcadmin'];
          const profiles = admin.split(',').map(p => p.trim()).filter(Boolean);
          const isOnlyKit = profiles.length > 0 && profiles.every(p => kitList.includes(p));
          if (isOnlyKit) {
            this.$router.push('/webapp/questionssimulado');
            return;
          }
        } catch (e) {
          console.error('Erro ao verificar admin no login:', e);
        }
        this.$router.push('/webapp/questions');
        return;
      }
    },

  },
  mounted() {
    if (this.$route.query.trial || this.$route.query.email) {
      this.showTrialDialog = true;
    }
  }
};
</script>

<style scoped>
.vs-login-wrapper {
  position: relative;
  background-color: #030a1a !important;
  min-height: 100vh;
  overflow: hidden;
  color: #fafaff;
}

/* Ambient glow orbs matching verisalvatori.com.br */
.vs-login-glow-1 {
  position: absolute;
  top: -150px;
  right: -100px;
  width: 650px;
  height: 650px;
  background: radial-gradient(circle at center, rgba(49, 95, 216, 0.25) 0%, rgba(3, 10, 26, 0) 70%);
  pointer-events: none;
  filter: blur(50px);
}

.vs-login-glow-2 {
  position: absolute;
  bottom: -150px;
  left: -100px;
  width: 700px;
  height: 700px;
  background: radial-gradient(circle at center, rgba(83, 126, 251, 0.18) 0%, rgba(3, 10, 26, 0) 70%);
  pointer-events: none;
  filter: blur(60px);
}

.vs-login-grid-bg {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(rgba(169, 188, 229, 0.08) 1px, transparent 1px);
  background-size: 32px 32px;
  pointer-events: none;
  opacity: 0.6;
}

/* Glassmorphic luxury card */
.vs-login-card {
  position: relative;
  background: rgba(5, 18, 46, 0.8) !important;
  backdrop-filter: blur(28px) saturate(140%) !important;
  -webkit-backdrop-filter: blur(28px) saturate(140%) !important;
  border: 1px solid rgba(169, 188, 229, 0.18) !important;
  box-shadow: 0 25px 60px -10px rgba(0, 0, 0, 0.65), 0 0 50px -10px rgba(49, 95, 216, 0.2) !important;
  border-radius: 28px !important;
  color: #fafaff;
  z-index: 2;
}

.vs-logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.vs-login-logo {
  max-width: 250px;
  height: auto;
  filter: drop-shadow(0 4px 20px rgba(131, 178, 255, 0.18));
}

.vs-login-subtitle {
  font-family: "Inter", sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.34em;
  color: #a5bada;
  margin-top: 9px;
  text-transform: uppercase;
}

.vs-login-tagline {
  font-family: "Roboto Condensed", sans-serif;
  font-size: 15px;
  color: #8296b9;
  letter-spacing: 0.02em;
  margin-top: 5px;
}

.vs-input-wrapper {
  position: relative;
}

.vs-input-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.04em;
  color: #aab8d4;
  margin-bottom: 6px;
}

.vs-forgot-link {
  background: none;
  border: none;
  padding: 0;
  color: #83b2ff;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s, text-decoration 0.2s;
}

.vs-forgot-link:hover {
  color: #bcd5ff;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.vs-btn-submit {
  height: 50px !important;
  background: linear-gradient(135deg, #315fd8 0%, #4f80fc 100%) !important;
  color: #ffffff !important;
  font-weight: 600 !important;
  font-size: 15px !important;
  letter-spacing: 0.03em !important;
  border-radius: 14px !important;
  box-shadow: 0 8px 24px -4px rgba(49, 95, 216, 0.5) !important;
  text-transform: none !important;
  transition: transform 0.2s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.2s cubic-bezier(0.22, 1, 0.36, 1) !important;
}

.vs-btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 30px -4px rgba(49, 95, 216, 0.65) !important;
}

.vs-btn-submit:active:not(:disabled) {
  transform: translateY(0);
}

.vs-btn-submit:disabled,
.vs-btn-submit.v-btn--disabled {
  opacity: 0.55 !important;
  color: #ffffff !important;
  box-shadow: none !important;
  cursor: not-allowed !important;
}

.vs-btn-submit:disabled *,
.vs-btn-submit.v-btn--disabled * {
  color: #ffffff !important;
}

.vs-divider-box {
  display: flex;
  align-items: center;
  gap: 14px;
}

.vs-divider-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(169, 188, 229, 0.2), transparent);
}

.vs-divider-text {
  font-size: 10px;
  letter-spacing: 0.22em;
  font-weight: 600;
  color: #7284a7;
}

.vs-trial-caption {
  font-size: 13px;
  line-height: 1.5;
  color: #9eb1d5;
  margin-bottom: 12px;
}

.vs-btn-trial {
  height: 48px !important;
  background: rgba(83, 126, 251, 0.12) !important;
  border: 1px solid rgba(131, 178, 255, 0.32) !important;
  color: #dce7ff !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.02em !important;
  border-radius: 14px !important;
  text-transform: none !important;
  transition: all 0.25s cubic-bezier(0.22, 1, 0.36, 1) !important;
}

.vs-btn-trial:hover {
  background: rgba(83, 126, 251, 0.22) !important;
  border-color: rgba(131, 178, 255, 0.55) !important;
  color: #ffffff !important;
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(83, 126, 251, 0.2) !important;
}

.vs-card-footer-note {
  font-size: 12px;
  color: #788cae;
  display: inline-flex;
  align-items: center;
}

:deep(.vs-custom-field .v-field) {
  background-color: rgba(3, 10, 26, 0.5) !important;
  border-radius: 12px !important;
  color: #ffffff !important;
  border-color: rgba(169, 188, 229, 0.25) !important;
  transition: border-color 0.2s, box-shadow 0.2s;
}

:deep(.vs-custom-field .v-field--focused) {
  border-color: #537efb !important;
  box-shadow: 0 0 0 3px rgba(83, 126, 251, 0.25) !important;
}

:deep(.vs-custom-field input) {
  color: #ffffff !important;
}

:deep(.vs-custom-field .v-icon) {
  color: #8296b9 !important;
}

:deep(.vs-custom-field .v-field--focused .v-icon) {
  color: #83b2ff !important;
}
</style>
